import {
  iAccommodationLegDetails,
  iAttractionTicketLegDetails,
  iCarHireLegDetails,
  iCruiseLegDetails,
  iExcursionLegDetails,
  iFlightLegDetails,
  iRailLegDetails,
  iTransferLegDetails
} from "../holiday-quotes-x/holiday-quotes-x-model";
import type { iExternalAccountingRefs } from "@sdk/accounting/external-accounting-ref";


// ---------------------------------------------------------------------------
// Types (union types - UPPER_SNAKE_CASE)
// ---------------------------------------------------------------------------

export type BookingStatus = "DRAFT" | "SENT" | "EXPIRED" | "CONFIRMED";

export type MarketingMedium =
  | "EMAIL"
  | "FACEBOOK"
  | "INSTAGRAM"
  | "ONLINE"
  | "TELEPHONE"
  | "WEBCHAT"
  | "WHATSAPP";

export type RequestType =
  | "CAR_HIRE"
  | "FLIGHT_ONLY"
  | "HOTEL_ONLY"
  | "PACKAGE"
  | "OTHER";

export type CommunicationMediumOfChoice = MarketingMedium;

export type PassengerType = "ADULT" | "CHILD" | "YOUTH" | "INFANT";

export type PaymentPlanType = "UPFRONT_PAYMENT" | "INSTALLMENT";

export type PaymentMethodType = string;

export type CardType = "VISA" | "MASTER" | "AMEX" | "OTHER";

export type AtolSaleCategory =
  | "FLIGHT_ONLY"
  | "PACKAGE_SINGLE"
  | "PACKAGE_MULTI"
  | "ATOL_TO_ATOL"
  | "AIRLINE_TICKET_AGENT"
  | "NON_LICENSABLE";

export interface iBookingAtol {
  atolSaleCategory?: AtolSaleCategory;
  isAtolProtected?: boolean;
  protectedPassengerIds?: string[];
  protectedProductIds?: string[];
  atolCertificateIssuedAt?: number;
  classificationConfirmedAt?: number;
  classificationConfirmedBy?: string;
}

export type ProductType =
  | "FLIGHT"
  | "ACCOMMODATION"
  | "CRUISE"
  | "CAR"
  | "TRANSFER"
  | "ATTRACTION"
  | "EXCURSION"
  | "RAIL"
  | "TOUR";

// NOTE: SupplierPaymentMethod was removed as part of multi-currency cleanup (Todo 14 / Phase 1).

// ---------------------------------------------------------------------------
// Nested interfaces
// ---------------------------------------------------------------------------

export interface iEnquiryData {
  customerId: string;
  customerName: string;
  email: string;
  mobileNumber: string;
  leadReceivedAt: number;

  marketingDealId: string;
  notes: string;

  // Added
  enquirySource: string;
  enquiryTags: string[];
  clickConnectorConversationId?: string;

  // Removed 
  // marketingMedium: MarketingMedium;
  // communicationMediumOfChoice: CommunicationMediumOfChoice;
  // enquiredMedium: MarketingMedium;

  // requestType: RequestType;
  requestType: string;
}

export interface iABTACoveredDateAndTime {
  notes: string;
  attachments: string[];
}

export interface iLeadProof {
  notes: string;
  attachments: string[];
}

export interface iBookingData {
  departureDate: number;
  departureAirport: string;
  arrivalDate: number;
  arrivalAirport: string;
  ABTACoveredDateAndTime: iABTACoveredDateAndTime;
  leadProof: iLeadProof;
  // From Templates
  emailTemplateId: string;
  greetings: string;
  travelAdvisory: string;
  travelAdvice?: string;
  checkInTimes?: string;
  otherInformation?: string;
  paymentPolicy: string;
  howToPay: string;
  importantInformation: string;
}

export interface iPassenger {
  id: string;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  passengerType: PassengerType;
  dateOfBirth: number;
  email: string;
  phoneNumber: string;
  notes: string;
  travelDocumentType: 'PASSPORT' | 'GOVERNMENT_ISSUED_ID' | 'OTHER';
  passportNationality: string;
  passportType: string;
  passportImage: string;
  specialRequest: string;
}

export interface iPaymentScheduleItem {
  id: string;
  amount: number;
  dueDate: number;
  label: string;
  remark?: string;
  remarkAttachments?: string[];
  remarkUpdatedAt?: number;
  remarkUpdatedBy?: string;
  reminderLastSentAt?: number;
}

export interface iPaymentPlan {
  type: PaymentPlanType;
  paymentSchedule: iPaymentScheduleItem[];
}


export interface iPricingLineItem {
  item: string;
  reference: string;
  quantity: number;
  amount: number;
}

export interface iPricingTaxSnapshot {
  ratePercent: number;
  treatment: string;
  priceEntryMode: "TAX_INCLUSIVE" | "TAX_EXCLUSIVE";
  overridden?: boolean;
  overrideReason?: string;
}

export interface iPricingBreakdown {
  /**
   * Currency the customer sees the booking quote in.
   */
  sellingCurrency: string;
  /** Net amount (ex VAT) in selling currency. */
  subtotal?: number;
  /** VAT amount in selling currency. */
  taxAmount?: number;
  totalCost: number;
  /** Booking/brand home currency at the time of pricing calculation. */
  homeCurrency?: string;
  /** `totalCost` converted into `homeCurrency`. */
  totalCostInHomeCurrency?: number;
  /** Net converted into `homeCurrency`. */
  subtotalInHomeCurrency?: number;
  /** VAT converted into `homeCurrency`. */
  taxAmountInHomeCurrency?: number;
  /** FX rate used for sellingCurrency -> homeCurrency conversion. */
  homeCurrencyExRate?: number;
  /** Unix ms timestamp for the FX rate used. */
  homeCurrencyExRateTimestamp?: number;
  tax?: iPricingTaxSnapshot;
  lineItems?: iPricingLineItem[];
}

export interface iPricingRevisionHistory {
  updatedAt: number;
  updatedBy: string;
  notes: string;
  pricingBreakdown: iPricingBreakdown;
  paymentPlan: iPaymentPlan;
  amendmentReference: string;
}

export interface iPricingAndPaymentsPlan {
  pricingBreakdown: iPricingBreakdown;
  paymentPlan: iPaymentPlan;
}

export type PaymentReminderEmailType =
  | "-7DAY"
  | "-3DAY"
  | "0DAY"
  | "+7DAY"
  | "CUSTOM";

export interface iPaymentReminderEmails {
  isActive: boolean;
  /** When true, automatic reminder emails are suspended until resumed by an agent. */
  isPaused?: boolean;
  /** Next reminder email due time (unix ms). Null means no next email scheduled. */
  nextReminderEmailAt: number | null;
  nextReminderEmailType: PaymentReminderEmailType | null;
  /** `iPaymentScheduleItem.id` this reminder is associated with (if applicable). */
  nextPaymentScheduleItemId: string | null;

  /** Metadata (optional) */
  lastSentAt?: number;
  lastSentType?: PaymentReminderEmailType;
  lastSentPaymentScheduleItemId?: string;
  lastError?: string;
}

/** Pricing draft + customer invoice pointer on the booking document. */
export interface iBookingPricingAndPayments {
  draft: iPricingAndPaymentsPlan;
  isCustomerInvoiceCreated?: boolean;
  customerInvoiceId?: string;
}

export interface iCustomerInvoice {
  id?: string;
  organizationId: string;
  brandId?: string;
  bookingId: string;
  bookingRef: string;
  customerId?: string;
  pricingBreakdown: iPricingBreakdown;
  paymentPlan: iPaymentPlan;
  paymentReminderEmails: iPaymentReminderEmails;
  revisionHistory: iPricingRevisionHistory[];
  totalPaid?: number;
  totalSurcharges?: number;
  totalPDQCharges?: number;
  accrualJournalEntryId?: string;
  externalRefs?: iExternalAccountingRefs;
  createdAt: number;
  createdBy: string;
  isFlagged?: boolean;
  flaggedAt?: number;
  flaggedBy?: string;
  flagNote?: string;
}

/** How PDQCharges on a receipt was determined. */
export type PdqChargeSource = "FORMULA" | "STRIPE" | "RECONCILIATION";

/** How a refund was settled. Omit or 'CASH' for legacy cash/bank refunds. */
export type RefundDisposition = "CASH" | "CREDIT_VOUCHER";

/** Customer payment receipt stored in its own collection. */
export interface iCustomerPaymentReceipt {
  id: string;
  organizationId: string;
  brandId?: string;
  bookingId: string;
  bookingRef: string;
  customerInvoiceId: string;

  paymentMethodId: string;
  paymentMethodType: PaymentMethodType;
  cardType?: CardType;
  cardBin?: string;
  date?: number;
  originalCurrency: string;
  originalAmount: number;
  isRefund?: boolean;
  refundDisposition?: RefundDisposition;
  issuedCreditVoucherId?: string;
  creditVoucherExpiryDate?: number;
  creditVoucherCodeOverride?: string;
  redeemedCreditVoucherId?: string;
  creditVoucherAppliedAmount?: number;
  homeCurrency: string;
  amountInHomeCurrency: number;
  homeCurrencyExRate: number;
  homeCurrencyExRateTimestamp: number;
  reference?: string;
  addedBy: string;
  addedAt: number;
  paymentScheduleItemId?: string;
  remark?: string;
  remarkAttachments?: string[];
  remarkUpdatedAt?: number;
  remarkUpdatedBy?: string;
  voided?: boolean;
  voidedAt?: number;
  voidedBy?: string;

  /** Surcharge or processing fee collected from the customer in original currency. */
  customerSurcharge?: number;
  /** Customer surcharge converted to home currency. */
  customerSurchargeInHomeCurrency?: number;

  PDQCharges?: number;
  PDQChargeFormula?: string;
  pdqChargeSource?: PdqChargeSource;

  requireReconcilation?: boolean;
  reconciledAt?: number;
  reconciledBy?: string;
  bankTransactionId?: string;
  journalEntryIds?: string[];
  pdqReconcileBookingTransactionId?: string;
  pdqReconcileJournalEntryIds?: string[];
  externalRefs?: iExternalAccountingRefs;
  isFlagged?: boolean;
  flaggedAt?: number;
  flaggedBy?: string;
  flagNote?: string;
}

/** Client create payload; home-currency fields are computed on the server. */
export type CustomerPaymentReceiptCreateInput = Pick<
  iCustomerPaymentReceipt,
  "paymentMethodId"
> &
  Partial<
    Pick<
      iCustomerPaymentReceipt,
      | "paymentMethodType"
      | "cardType"
      | "cardBin"
      | "date"
      | "originalCurrency"
      | "originalAmount"
      | "reference"
      | "addedBy"
      | "addedAt"
      | "paymentScheduleItemId"
      | "remark"
      | "remarkAttachments"
      | "refundDisposition"
      | "creditVoucherExpiryDate"
      | "creditVoucherCodeOverride"
      | "redeemedCreditVoucherId"
      | "creditVoucherAppliedAmount"
      | "customerSurcharge"
    >
  >;

/** Net holiday payment in home currency (gross collected minus customer surcharge). */
export function getReceiptHolidayAmount(
  receipt: Pick<
    iCustomerPaymentReceipt,
    "amountInHomeCurrency" | "customerSurchargeInHomeCurrency"
  >
): number {
  return (
    Number(receipt.amountInHomeCurrency ?? 0) -
    Number(receipt.customerSurchargeInHomeCurrency ?? 0)
  );
}

export interface iProductPriceDetails {
  /** Supplier/original currency for this product price. */
  originalCurrency: string;
  /** Supplier/original amount for this product price (buy amount). */
  originalAmount: number;

  /** Home currency for accounting values. Must match booking.homeCurrency. */
  homeCurrency: string;
  supplierGrossInHomeCurrency: number;
  homeCurrencyExRate: number;
  homeCurrencyExRateTimestamp: number;
}

export interface iFlightPaymentCardNotes {
  // The below fields might not be required - START  - Decision for later
  voucherValue: string;
  netFareCalculation: string;
  profitAfterDeduction: string;
  // The below fields might not be required - END
  fareTypeCode: string;
  cancellationCost: string;
  dateChangeCost: string;
  fareAndPNRCancellationDate: string;
  supplierReference: string;
  FlightDetailsAndSuppliers: string;
}

export interface iBackFlightOfficeData {
  ticketNo?: string;
  ticketDate?: number;
  pnr?: string;
  gds?: string;
  iataNo?: string;
  type?: string;
  ticketingDeadline?: number;
  fareExpiryDate?: number;
  validatingAirline?: string;
  departureCity?: string;
  arrivalCity?: string;
  journey?: string;
  interInd?: string;
  baggage?: string;
  seat?: string;
  fareType?: string;
  airCode?: string;
  issuedBy?: string;
  bookedBy?: string;
  pcc?: string;
  validFrom?: number;
  validTo?: number;
  unusedTicket?: boolean;
  commOn?: string;
  vLocator?: string;
}

export interface iProductFlight {
  data: iFlightLegDetails[];
  // Flight Specific Fields
  passengerId?: string;
  originalPNR: string;
  baggageDetails: string;
  handLuggageRestrictions: string;
  baggageInformation: string;
  paymentsCardNotes: iFlightPaymentCardNotes;
  backOfficeData: iBackFlightOfficeData;
}

export interface iProductAccommodation {
  data: iAccommodationLegDetails;
}

export interface iProductCruise {
  data: iCruiseLegDetails;
}

export interface iProductCar {
  data: iCarHireLegDetails;
}

export interface iProductTransfer {
  data: iTransferLegDetails;
}

export interface iProductAttraction {
  data: iAttractionTicketLegDetails;
}

export interface iProductExcursion {
  data: iExcursionLegDetails;
}

export interface iProductRail {
  data: iRailLegDetails;
}

/** Tour/excursion item on a booking product. Extensible via index signature. */
export interface iProductTour {
  data: {
    name?: string;
    description?: string;
    date?: number;
    attachments?: string[];
    notes?: string;
  };
}

export interface iProductVoucher {
  isReady?: boolean;
  voucherDueDate?: number;
  body: string;
  attachments: string[];
  updatedAt: number;
  updatedBy: string;
}

/** Shared fields for every booking product. */
export interface iProductBase {
  id: string;
  type: ProductType;
  isHiddenProduct: boolean;
  linkedPassengerIds?: string[];
  parentProductId?: string;

  // Supplier Details
  supplierId: string;
  priceDetails: iProductPriceDetails;
  isBilled?: boolean;
  supplierBillId?: string;
  supplierBookingReference?: string;
  supplierBookingInternalNotes?: string;
  // Voucher Details
  voucher?: iProductVoucher;
  finalVouchersSentAt?: number;
  finalVouchersSentBy?: string;

  // Voiding Details
  voided?: boolean;
  voidedAt?: number;
  voidedBy?: string;

  // Pre - Confirmation
  supplierProductReference?: string;

  // Customer Notes
  customerNotes?: string;
  customerNotesAttachments?: string[];

  // Internal Notes
  internalNotesAttachments: string[];
  internalNotes: string;
}

/**
 * A single product on a booking (flight, accommodation, cruise, car, transfer, attraction, excursion, rail, or tour).
 * Exactly one of the product-type fields should be set according to type;
 * for type TOUR, use the tour field.
 */
export interface iProduct extends iProductBase {
  flight?: iProductFlight;
  accommodation?: iProductAccommodation;
  cruise?: iProductCruise;
  car?: iProductCar;
  transfer?: iProductTransfer;
  attraction?: iProductAttraction;
  excursion?: iProductExcursion;
  rail?: iProductRail;
  tour?: iProductTour;
}

// ---------------------------------------------------------------------------
// Product type guards
// ---------------------------------------------------------------------------

export function isFlightProduct(
  p: iProduct
): p is iProduct & { flight: iProductFlight } {
  return p.type === "FLIGHT" && p.flight != null;
}

export function isAccommodationProduct(
  p: iProduct
): p is iProduct & { accommodation: iProductAccommodation } {
  return p.type === "ACCOMMODATION" && p.accommodation != null;
}

export function isCruiseProduct(
  p: iProduct
): p is iProduct & { cruise: iProductCruise } {
  return p.type === "CRUISE" && p.cruise != null;
}

export function isCarProduct(
  p: iProduct
): p is iProduct & { car: iProductCar } {
  return p.type === "CAR" && p.car != null;
}

export function isTransferProduct(
  p: iProduct
): p is iProduct & { transfer: iProductTransfer } {
  return p.type === "TRANSFER" && p.transfer != null;
}

export function isAttractionProduct(
  p: iProduct
): p is iProduct & { attraction: iProductAttraction } {
  return p.type === "ATTRACTION" && p.attraction != null;
}

export function isExcursionProduct(
  p: iProduct
): p is iProduct & { excursion: iProductExcursion } {
  return p.type === "EXCURSION" && p.excursion != null;
}

export function isRailProduct(
  p: iProduct
): p is iProduct & { rail: iProductRail } {
  return p.type === "RAIL" && p.rail != null;
}

export function isTourProduct(
  p: iProduct
): p is iProduct & { tour: iProductTour } {
  return p.type === "TOUR" && p.tour != null;
}

export type AmendmentType =
  | "DATE_CHANGE"
  | "CANCELLATION"
  | "PRODUCT_CHANGE"
  | "VOLUNTARY_CANCELLATION"
  | "INVOLUNTARY_CANCELLATION"
  | "REFUND"
  | "PASSENGER_CHANGE"
  | "OTHER";

export interface iAmendmentRefundDetails {
  eligibleForRefund?: boolean;
  refundAmount?: number;
  refundCurrency?: string;
  reason?: string;
  estimatedTimeframe?: string;
  refundDestination?: string;
}

export interface iAmendment {
  id: string;
  status: "REQUESTED" | "DRAFT" | "SENT" | "CONFIRMED" | "DISCARDED";
  type: AmendmentType;
  /** Who requested the amendment (e.g. customer userId). */
  requestedBy: string;
  /** When the amendment was requested (Unix ms). */
  requestedAt: number;
  /** Customer's request text (set when amendment is requested). */
  amendmentRequestBody: string;
  /** Agent notes/summary (set when agent applies the amendment). */
  body: string;
  /** When the agent applied the amendment (Unix ms). 0 if not yet applied. */
  amendmentAddedAt: number;
  /** Who applied the amendment (e.g. agent userId). */
  amendmentAddedBy: string;
  /** Product IDs from booking.products or a previous amendment.products. */
  affectedProducts: string[];
  /** Product data after the amendment (when agent has applied it). */
  products: iProduct[];
  /** Price details after the amendment (set when agent applies the amendment). */
  amendedPrice?: iPricingAndPaymentsPlan;
  lastPricingAndPaymentsSnapshot: iPricingAndPaymentsPlan;

  // Updates to Passenger Details
  updatedPassengerDetails?: iPassenger[];
  lastPassengerDetailsSnapshot?: iPassenger[];
  /** Passenger IDs cancelled/removed by this amendment. */
  cancelledPassengerIds?: string[];
  /** Reason for cancellation / refund (agent-entered). */
  cancellationReason?: string;
  /** Structured refund details shown in confirmation email and dashboard. */
  refundDetails?: iAmendmentRefundDetails;
  /** When the customer confirmed (Unix ms). 0 if not yet confirmed. */
  confirmedAt: number;
  /** Customer's confirmation text (set when amendment is confirmed). */
  confirmationBody: string;
  confirmationTrace: unknown;
  /** Whether the amendment request was discarded (e.g. by agent). */
  isDiscarded: boolean;
  /** When the amendment was discarded (Unix ms). 0 if not discarded. */
  discardedAt: number;
  /** Who discarded the amendment (e.g. agent userId). */
  discardedBy: string;
}

export type {
  iBookingActivityLog,
  iActivityLog,
} from "./booking-activity-log-model";
export type {
  iBookingDiscussionItem,
  iDiscussionItem,
} from "./booking-discussion-item-model";

export interface iPassengerDetailsRevision {
  updatedAt: number;
  updatedBy: string;
  notes: string;
  passengers: iPassenger[];
  amendmentReference: string;
}

export interface iPassengerDetails {
  passengers: iPassenger[];
  revisions: iPassengerDetailsRevision[];
}

// ---------------------------------------------------------------------------
// Main booking interface
// ---------------------------------------------------------------------------

export interface iBookingFlag {
  isActive: boolean;
  raisedBy: string;
  raisedAt: number;
  comment: string;
  requireValidation: boolean;
  requireComment: boolean;
}

export interface iBookingFlags {
  redFlag: iBookingFlag;
  blueFlag: iBookingFlag;
}

export interface iBookingMember {
  userId: string;
  joinedTime: number;
}

export interface iBooking {
  id?: string;
  shortId?: number;
  bookingRef?: string;
  holidayQuoteId?: string;
  customerId?: string;
  opportunityId?: string;
  organizationId: string;
  /**
   * Phase 2/4 rollout: brand association (required after migration).
   */
  brandId?: string;
  /** Booking accounting currency snapshot (from brand.homeCurrency at creation time). */
  homeCurrency?: string;
  status: BookingStatus;
  createdAt: number;
  /** User ID of the user who created the booking. */
  createdBy: string;
  owner: string;
  members: iBookingMember[];
  businessAreaId: string;
  enquiryData: iEnquiryData;
  bookingData: iBookingData;
  passengerDetails: iPassengerDetails;
  pricingAndPayments: iBookingPricingAndPayments;
  products: iProduct[];
  amendments: iAmendment[];
  flags: iBookingFlags;

  /** Booking-level internal tags (admin only). */
  adminTags: string[];
  /** Booking-level customer/ops tags. */
  tags: string[];
  /** ATOL regulatory classification and certificate metadata. */
  atol?: iBookingAtol;
  // Email Thread
  unreadCount: number;
  // Meta Data
  latestBookingConfirmationEmailSentAt?: number;
  latestVouchersEmailSentAt?: number;
  // Derived Data from the booking object - Calculated fields
  statusSummary?:
  | BookingStatus
  | "AMENDMENT_REQUESTED"
  | "AMENDMENT_DRAFTED"
  | "AMENDMENT_SENT"
  | "VOIDED"
  | "CLOSED";
  voided?: boolean;
  voidedAt?: number;
  voidedBy?: string;
  voidBookingTransactionId?: string;
  closed?: boolean;
  closedAt?: number;
  closedBy?: string;
  closeBookingTransactionId?: string;
  // Supplier payment calculated fields (products + amendments.products)
  totalSupplierPayments?: number;
  totalSupplierPaymentsMade?: number;
  nextSupplierPaymentDue?: number;
  areSupplierPaymentsCompleted?: boolean;
  // Customer payments (derived from CustomerPaymentReceipt collection)
  totalPaymentsReceived?: number;
  paymentSummary?: "NOT_PAID" | "PARTIALLY_PAID" | "FULLY_PAID";
  paymentDueSummary?: "NOT_DUE" | "DUE";
  // Voucher due calculated fields (products + amendments.products)
  nextVoucherIssuanceDueDate?: number;
  voucherDueStatus?: "NOT_DUE" | "DUE" | "OVERDUE";
  voucherStatusSummary?: "NOT_ISSUED" | "PARTIALLY_ISSUED" | "FULLY_ISSUED";
  /** Live gross/net profit estimate; recomputed on booking save. */
  profitabilityEstimate?: iBookingProfitabilityEstimate;
  profitAllocation?: iBookingProfitAllocation;
}

export interface iBookingProfitAllocation {
  /** True after first successful profit allocation. Stays true on re-allocate. */
  status: boolean;
  /** Gross profit (invoice − cost) at time of allocation, home currency. */
  grossProfitDuringAllocation: number;
  /** Net profit at time of allocation, home currency. */
  netProfitDuringAllocation: number;
  /** Epoch ms when profit was last allocated. */
  allocatedAt: number;
}

/** Live profitability estimate denormalized on the booking document. */
export interface iBookingProfitabilityEstimate {
  /** Invoice amount in home currency. */
  invoiceAmount: number;
  /** Total supplier cost in home currency. */
  totalCost: number;
  /** Gross profit (invoice − cost) in home currency. */
  grossProfit: number;
  /** Sum of brand-configured other costs deducted from gross profit. */
  otherCostsTotal: number;
  /** Estimated net profit (gross − other costs) in home currency. */
  netProfit: number;
  /** Epoch ms when this estimate was last recomputed. */
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// TA Desk Booking Email Messages (Resend)
// ---------------------------------------------------------------------------

export type BookingEmailMessageDirection = "inbound" | "outbound";

export type BookingEmailStatus =
  | "sent"
  | "delivered"
  | "delivery_delayed"
  | "received"
  | "complained"
  | "bounced"
  | "opened"
  | "clicked"
  | "not_started";

export interface iBookingEmailEvent {
  type?: string;
  event?: string;
  timestamp?: number;
  createdAt?: number;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface iBookingEmailMessage {
  id?: string;
  organizationId: string;
  /**
   * Phase 2/3 rollout: brand association (required after migration).
   */
  brandId?: string;
  bookingId: string;
  // Email meta
  subjectLine: string;
  from: string;
  to: string;
  cc: string[];
  bcc: string[];
  threadId?: string;
  messageId?: string;
  // Email body
  strippedText?: string;
  strippedHtml?: string;
  fullText?: string;
  fullHtml?: string;
  /** Raw received email content (e.g. .eml string), downloaded from Resend receiving API. */
  rawEmail?: string;
  // Attachments (URLs or keys)
  attachments: string[];
  // Meta
  timestamp: number;
  fromCustomer: boolean;
  sendingUserId: string;
  isRead: boolean;
  readBy?: string;
  direction?: BookingEmailMessageDirection;
  resendEmailId?: string;
  // Resend lifecycle (for frontend display)
  /** Last known delivery/engagement status from Resend webhooks */
  emailStatus?: BookingEmailStatus;
  /** Chronological list of Resend events (sent → delivered → opened, etc.) */
  emailEvents?: iBookingEmailEvent[];
}
